// 接入代码生成器纯函数模块(05§4.13 / 13§6.5)。
// 把原 CodeGenerator.tsx 内嵌的生成逻辑抽成无副作用、参数 schema 驱动的纯函数,
// 便于单测与复用;行为与原内联实现保持一致。

export type UserMode = "magic" | "iframe";

// 统一 SDK 入口:按需加载器 hongmo.js(内核 + analytics/annotate/copilot/chat 模块)。
// 旧 agent-widget.js 已转为转发到 hongmo.js 的薄壳;新接入一律产出 hongmo.js。
export const HONGMO_SDK_SRC = "https://ai.zhheo.com/static/public/hongmo.js";
// 兼容旧引用名(已弃用),等同 HONGMO_SDK_SRC。
export const AGENT_WIDGET_SRC = HONGMO_SDK_SRC;

// 默认配置参数类型和值(生成器以此判定"非默认才输出")。
export const defaultConfigParams = {
  postSelector: "这里修改为指定参数，详见页面底部的摘要参数配置教程",
  postURL: "*",
  blacklist: "",
  wordLimit: 1000,
  typingAnimate: true,
  title: "宇宙无敌智能摘要",
  injectDom: "",
  beginningText: "",
  loadingText: true,
  backgroundColor: "#3e86f6",
  bottom: "16px",
  left: "16px",
  fill: "#FFFFFF",
  width: "44px",
  frameWidth: "375px",
  frameHeight: "600px",
  defaultInput: true,
  showInviteLink: false,
  theme: "default", // 改为 'default' 而不是空字符串
  userMode: "magic" as UserMode, // 显式指定类型
  upLoadWeb: true, // 添加新的默认配置
  userTitle: "", // 修改默认值为空字符串
  userDesc: "", // 添加默认描述，默认为空
  userIcon: "", // 添加默认图标，默认为空
  defaultChatQuestions: [] as string[],
  defaultSearchQuestions: [] as string[],
  addButton: true, // 添加默认配置，默认显示按钮
  blackDom: [] as string[], // 添加默认配置，默认为空数组
  hotWords: true, // 新增热词功能，默认为打开
  recommend: 0, // 文章底部推荐文章数量，0-10，0表示不显示，默认关闭
  tianliGPT_podcast: false, // AI播客功能，默认关闭
};

// 不加 as const:配置项在运行时可变,需要 widened(string/number/boolean)类型而非字面量类型。
export type ConfigParams = typeof defaultConfigParams;

// 生成输入 schema:页面状态映射为纯函数入参。
export interface CodeGenInput {
  key: string;
  articleSummary: boolean;
  chatAssistant: boolean;
  configParams: ConfigParams;
  agentShareToken?: string;
}

export interface GeneratedCode {
  unified: string;
  legacy: string;
}

const escapeAttribute = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// generateOptionalParam:仅当值与默认不同时输出 `let tianliGPT_<key> = <value>;`。
export const generateOptionalParam = (key: string, value: any, defaultValue: any) => {
  // 特殊处理主题参数，当为 'default' 时不生成代码
  if (key === "theme" && value === "default") return "";

  if (value === defaultValue || value === "" || value === undefined) return "";

  if (typeof value === "boolean") {
    return `\nlet tianliGPT_${key} = ${value};`;
  } else if (typeof value === "number") {
    return `\nlet tianliGPT_${key} = ${value};`;
  } else {
    return `\nlet tianliGPT_${key} = '${value}';`;
  }
};

export const generateAgentWidgetCode = (shareToken: string) => `<script>
(function () {
  if (window.__HongMoAgentWidget) return;
  window.__HongMoAgentWidget = true;
  var apiBase = 'https://api.ai.zhheo.com';
  var shareToken = '${shareToken}';
  var box = document.createElement('div');
  box.style.cssText = 'position:fixed;right:24px;bottom:24px;z-index:2147483647;font-family:system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif';
  var panel = document.createElement('div');
  panel.style.cssText = 'display:none;width:320px;height:420px;margin-bottom:12px;background:#fff;border:1px solid #e5e7eb;border-radius:8px;box-shadow:0 18px 40px rgba(15,23,42,.18);overflow:hidden';
  var button = document.createElement('button');
  button.textContent = 'AI';
  button.style.cssText = 'width:48px;height:48px;border-radius:24px;border:0;background:#111827;color:#fff;font-weight:700;cursor:pointer;box-shadow:0 10px 25px rgba(0,0,0,.18)';
  panel.innerHTML = '<div style="padding:12px;border-bottom:1px solid #e5e7eb;font-weight:600">智能助手</div><div data-log style="height:300px;overflow:auto;padding:12px;font-size:14px;line-height:1.5"></div><form style="display:flex;gap:8px;padding:10px;border-top:1px solid #e5e7eb"><input name="q" autocomplete="off" style="flex:1;border:1px solid #d1d5db;border-radius:6px;padding:8px"/><button style="border:0;background:#111827;color:white;border-radius:6px;padding:0 12px">发送</button></form>';
  button.onclick = function () { panel.style.display = panel.style.display === 'none' ? 'block' : 'none'; };
  panel.querySelector('form').onsubmit = function (event) {
    event.preventDefault();
    var input = this.q;
    var text = input.value.trim();
    if (!text) return;
    input.value = '';
    var log = panel.querySelector('[data-log]');
    log.innerHTML += '<div><b>我:</b> ' + escapeHtml(text) + '</div>';
    fetch(apiBase + '/api/v3/agents/share/' + encodeURIComponent(shareToken) + '/run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: text, visitorId: getVisitorId() })
    }).then(function (response) {
      return response.json();
    }).then(function (result) {
      var answer = result && result.data && result.data.answer ? result.data.answer : result.msg || '';
      log.innerHTML += '<div style="margin-top:8px"><b>AI:</b> ' + escapeHtml(answer) + '</div>';
      log.scrollTop = log.scrollHeight;
    });
  };
  function getVisitorId() {
    try {
      var key = 'hongmo_agent_vid';
      var value = localStorage.getItem(key);
      if (!value) {
        value = Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem(key, value);
      }
      return value;
    } catch (error) {
      return 'anonymous';
    }
  }
  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }
  box.appendChild(panel);
  box.appendChild(button);
  document.body.appendChild(box);
})();
</script>`;

// 统一 SDK 片段(data-* 优先,推荐格式)。
const generateUnifiedSdkCode = (input: CodeGenInput): string => {
  const { key, articleSummary, chatAssistant, configParams } = input;
  const dataAttrs: Record<string, string> = {
    "project-key": key,
    "enable-summary": articleSummary ? "true" : "false",
    "enable-chat": chatAssistant ? "true" : "false",
  };

  // Share tokens are not part of the V4 runtime model. The SDK resolves the
  // project's primary Agent server-side from data-project-key.
  if (articleSummary) {
    dataAttrs["post-selector"] = configParams.postSelector;
    dataAttrs["post-url"] = configParams.postURL;
    dataAttrs["word-limit"] = String(configParams.wordLimit);
    dataAttrs["summary-theme"] = configParams.theme;
  }
  if (chatAssistant) {
    dataAttrs["chat-mode"] = configParams.userMode;
    dataAttrs["chat-add-button"] = String(configParams.addButton);
  }

  const attrs = Object.entries(dataAttrs)
    .filter(([, value]) => value !== "" && value !== undefined)
    .map(([name, value]) => `data-${name}="${escapeAttribute(value)}"`)
    .join("\n  ");

  return `<script
  async
  ${attrs}
  src="${HONGMO_SDK_SRC}">
</script>`;
};

// postChatConfig 对象(仅含非默认项)。
const generatePostChatConfig = (cp: ConfigParams, chatAssistant: boolean, articleSummary: boolean): string => {
  const config: Record<string, any> = {};

  if (cp.blackDom.length > 0 && chatAssistant && !articleSummary) {
    config.blackDom = cp.blackDom;
  }

  if (cp.addButton !== true) config.addButton = cp.addButton;
  if (cp.backgroundColor !== "#3e86f6") config.backgroundColor = cp.backgroundColor;
  if (cp.bottom !== "16px") config.bottom = cp.bottom;
  if (cp.left !== "16px") config.left = cp.left;
  if (cp.fill !== "#FFFFFF") config.fill = cp.fill;
  if (cp.width !== "44px") config.width = cp.width;
  if (cp.frameWidth !== "375px") config.frameWidth = cp.frameWidth;
  if (cp.frameHeight !== "600px") config.frameHeight = cp.frameHeight;
  if (cp.defaultInput !== true) config.defaultInput = cp.defaultInput;
  if (cp.showInviteLink !== false) config.showInviteLink = cp.showInviteLink;
  if (cp.upLoadWeb !== true) config.upLoadWeb = cp.upLoadWeb;
  if (cp.userMode !== "magic") config.userMode = cp.userMode;
  if (cp.userTitle !== "") config.userTitle = cp.userTitle;
  if (cp.userDesc !== "") config.userDesc = cp.userDesc;
  if (cp.userIcon !== "" && cp.userMode === "magic") {
    config.userIcon = cp.userIcon;
  }
  if (cp.hotWords !== true) config.hotWords = cp.hotWords;
  const recommendVal = Math.min(10, Math.max(0, Number(cp.recommend) || 0));
  if (recommendVal !== 0) config.recommend = recommendVal;

  if (cp.userMode === "magic") {
    if (cp.defaultChatQuestions.length > 0) {
      config.defaultChatQuestions = cp.defaultChatQuestions;
    }
    if (cp.defaultSearchQuestions.length > 0) {
      config.defaultSearchQuestions = cp.defaultSearchQuestions;
    }
  }

  if (Object.keys(config).length > 0) {
    return `var postChatConfig = ${JSON.stringify(config, null, 2)};`;
  }
  return "";
};

// 老格式:摘要脚本(tianli_gpt)。
const generateArticleSummaryCode = (key: string, cp: ConfigParams): string =>
  `<link rel="stylesheet" href="https://ai.zhheo.com/static/public/tianli_gpt.min.css">
<script>
let tianliGPT_postSelector = '${cp.postSelector}';${generateOptionalParam("postURL", cp.postURL, "*")}${generateOptionalParam("blacklist", cp.blacklist, "")}${generateOptionalParam("wordLimit", Number(cp.wordLimit), 1000)}${generateOptionalParam("typingAnimate", cp.typingAnimate, true)}${generateOptionalParam("Title", cp.title, "宇宙无敌智能摘要")}${generateOptionalParam("injectDom", cp.injectDom, "")}${generateOptionalParam("BeginningText", cp.beginningText, "")}${generateOptionalParam("loadingText", cp.loadingText, true)}${generateOptionalParam("theme", cp.theme, "")}
let tianliGPT_key = '${key}';
</script>
<script src="https://ai.zhheo.com/static/public/tianli_gpt.min.js"></script>`;

// generateCode:输入 schema -> {统一片段, 老格式片段}。
export function generateCode(input: CodeGenInput): GeneratedCode {
  const { key, articleSummary, chatAssistant, configParams: cp } = input;

  let legacy = "";
  if (false) {
    const agentWidgetCode = generateAgentWidgetCode("");
    legacy = articleSummary
      ? `${generateArticleSummaryCode(key, cp)}\n\n${agentWidgetCode}`
      : agentWidgetCode;
  } else if (chatAssistant && articleSummary) {
    legacy = `<link rel="stylesheet" href="https://ai.zhheo.com/static/public/postChatUser_summary.min.css">
<script>
let tianliGPT_postSelector = '${cp.postSelector}';${generateOptionalParam("postURL", cp.postURL, "*")}${generateOptionalParam("blacklist", cp.blacklist, "")}${generateOptionalParam("wordLimit", Number(cp.wordLimit), 1000)}${generateOptionalParam("typingAnimate", cp.typingAnimate, true)}${generateOptionalParam("Title", cp.title, "宇宙无敌智能摘要")}${generateOptionalParam("injectDom", cp.injectDom, "")}${generateOptionalParam("BeginningText", cp.beginningText, "")}${generateOptionalParam("loadingText", cp.loadingText, true)}${generateOptionalParam("theme", cp.theme, "")}
let tianliGPT_recommendation = true;${generateOptionalParam("podcast", cp.tianliGPT_podcast, false)}
${generatePostChatConfig(cp, chatAssistant, articleSummary)}
</script>
<script data-postChat_key="${key}" src="https://ai.zhheo.com/static/public/postChatUser_summary.min.js"></script>`;
  } else if (chatAssistant) {
    legacy = `<script>${generateOptionalParam("postURL", cp.postURL, "*")}${generateOptionalParam("podcast", cp.tianliGPT_podcast, false)}
${generatePostChatConfig(cp, chatAssistant, articleSummary)}
</script>
<script data-postChat_key="${key}" src="https://ai.zhheo.com/static/public/postChatUser.min.js"></script>`;
  } else if (articleSummary) {
    legacy = generateArticleSummaryCode(key, cp);
  }

  return { unified: generateUnifiedSdkCode(input), legacy };
}
